<template>
  <mpf-form
    v-if="formConfig"
    :form-title="formConfig.config?.formTitle"
    :hint="formConfig.config?.hint"
    :fields="formConfig.fields"
    :form-errors="formErrors"
    submit-label="Continue"
    @submit="handleSubmit"
  />
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useRoute } from 'vue-router';
  import MpfForm from '../components/MpfForm/MpfForm.vue';
  import { useRouteMeta } from '../composables/useRouteMeta';
  import { router } from '../../router';
  import { authenticateQuestionnaireApi } from '../../services/questionnaire.service';
  import { useErrorStore } from '../../stores/useErrorStore';
  import { MpfFormPropsWithConfig } from '../../types/router-meta';
  import type { FormError } from '../components/MpfForm/MpfForm.props';

  const route = useRoute();
  const errorStore = useErrorStore();
  const { formConfig } = useRouteMeta<MpfFormPropsWithConfig>();

  const formErrors = ref<FormError[]>([]);

  const handleSubmit = async (data: { values: Record<string, any>; valid: boolean }) => {
    if (!data.valid) return;

    formErrors.value = [];

    const result = await authenticateQuestionnaireApi(String(route.params.requestId), {
      firstName: data.values.firstName,
      lastName: data.values.lastName,
      postcode: data.values.postcode,
      dateOfBirth: data.values.dateOfBirth,
    });

    if (!result.ok) {
      // Details that match no record stay on the page — bouncing to the error
      // page would lose everything they typed over one mistyped character.
      errorStore.setErrors(result.errors ?? [{ code: 'single-patient-not-found' }]);

      formErrors.value = [
        {
          name: 'firstName',
          errors: [
            errorStore.getFirstMessage() ||
              'We could not find a patient record matching those details.',
          ],
        },
      ];
      return;
    }

    // Tenant taken from the route rather than left to patchTenantAwarePush.
    await router.push(`/${route.params.tenantId}/questionnaire/${route.params.requestId}/form`);
  };
</script>
